import type {
  MarkerInitData,
  MarkerStyle,
  MarkerUpdate,
  SerializableCluster,
} from "../workers/dispatch/types";

export const DEFAULT_MARKER_STYLE: MarkerStyle = {
  color: "#141414",
  opacity: 1,
  routePosition: null,
  size: 20,
};

function stylesEqual(a: MarkerStyle, b: MarkerStyle): boolean {
  return (
    a.color === b.color &&
    a.opacity === b.opacity &&
    a.size === b.size &&
    a.routePosition === b.routePosition
  );
}

// Positions for each marker (orderId → primary lat/lng).
// Used by Phase 3 components to avoid re-deriving coords from order data.
type MarkerPosition = { lat: number; lng: number };

export type CanvasMarkerEntry = {
  iconKey: string;
  lat: number;
  lng: number;
  markerId: string;
  orderId: string;
  style: MarkerStyle;
};

class MapRenderer {
  // ---- Write path (called from renderLoop) ----

  applyClusters(
    clusters: SerializableCluster[],
    individualOrderIds: string[],
  ): void {
    this.clusters = clusters;
    this.individualOrderIds = new Set(individualOrderIds);
    this.visibleOrderIds = new Set(individualOrderIds);
    this.notify();
  }

  applyInit(markers: MarkerInitData[]): void {
    this.styles.clear();
    this.positions.clear();
    this.canvasMarkers.clear();
    this.orderToMarkers.clear();

    for (const m of markers) {
      this.styles.set(m.orderId, m.style);
      // For INDIVIDUAL orders there are two markers; last write wins for position
      // which is the dropoff — that's fine for the card display use-case.
      this.positions.set(m.orderId, { lat: m.lat, lng: m.lng });

      this.canvasMarkers.set(m.markerId, {
        iconKey: m.iconKey,
        lat: m.lat,
        lng: m.lng,
        markerId: m.markerId,
        orderId: m.orderId,
        style: m.style,
      });
      const ids = this.orderToMarkers.get(m.orderId) ?? [];
      if (!ids.includes(m.markerId)) ids.push(m.markerId);
      this.orderToMarkers.set(m.orderId, ids);
    }

    this.notify();
  }

  applyUpdates(updates: MarkerUpdate[]): void {
    let changed = false;

    for (const u of updates) {
      const existing = this.styles.get(u.orderId);

      if (!existing || !stylesEqual(existing, u.style)) {
        this.styles.set(u.orderId, u.style);
        changed = true;
      }

      const existingPos = this.positions.get(u.orderId);
      if (
        !existingPos ||
        existingPos.lat !== u.lat ||
        existingPos.lng !== u.lng
      ) {
        this.positions.set(u.orderId, { lat: u.lat, lng: u.lng });
        changed = true;
      }

      const existing2 = this.canvasMarkers.get(u.markerId);
      if (
        !existing2 ||
        existing2.lat !== u.lat ||
        existing2.lng !== u.lng ||
        existing2.iconKey !== u.iconKey ||
        !stylesEqual(existing2.style, u.style)
      ) {
        this.canvasMarkers.set(u.markerId, {
          iconKey: u.iconKey,
          lat: u.lat,
          lng: u.lng,
          markerId: u.markerId,
          orderId: u.orderId,
          style: u.style,
        });
        changed = true;
      }

      // Register marker ID in orderToMarkers so getCanvasMarkerData can find it
      const markerIds = this.orderToMarkers.get(u.orderId) ?? [];
      if (!markerIds.includes(u.markerId)) {
        markerIds.push(u.markerId);
        this.orderToMarkers.set(u.orderId, markerIds);
      }
    }

    if (changed) this.notify();
  }

  // Per-marker canvas data (markerId → entry)
  private canvasMarkers = new Map<string, CanvasMarkerEntry>();
  private clusters: SerializableCluster[] = [];

  // ---- Read path (called from React via useSyncExternalStore) ----

  getCanvasMarkerData(): CanvasMarkerEntry[] {
    const result: CanvasMarkerEntry[] = [];
    for (const orderId of this.individualOrderIds) {
      const markerIds = this.orderToMarkers.get(orderId) ?? [];
      for (const mId of markerIds) {
        const entry = this.canvasMarkers.get(mId);
        if (entry) result.push(entry);
      }
    }
    return result;
  }

  getClusters(): SerializableCluster[] {
    return this.clusters;
  }

  getPosition(orderId: string): MarkerPosition | undefined {
    return this.positions.get(orderId);
  }

  getSnapshot = (): { version: number } => this.snapshot;

  getStyle(orderId: string): MarkerStyle {
    return this.styles.get(orderId) ?? DEFAULT_MARKER_STYLE;
  }

  private individualOrderIds: Set<string> = new Set();

  isVisible(orderId: string): boolean {
    if (this.visibleOrderIds.size === 0) return true; // no viewport filter yet
    return this.visibleOrderIds.has(orderId);
  }

  private notify(): void {
    this.snapshotVersion++;
    this.snapshot = { version: this.snapshotVersion };
    for (const cb of this.subscribers) cb();
  }

  // Reverse map: orderId → markerId[] for lookups
  private orderToMarkers = new Map<string, string[]>();
  private positions = new Map<string, MarkerPosition>();
  // useSyncExternalStore-compatible snapshot — returns a stable object reference
  // only when something changed (notify() was called). React compares by reference.
  private snapshot = { version: 0 };
  private snapshotVersion = 0;
  // Stable style objects per orderId — only replaced when style actually changes.
  // React's useSyncExternalStore relies on reference equality for bail-out.
  private styles = new Map<string, MarkerStyle>();

  subscribe = (callback: () => void): (() => void) => {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  };

  private subscribers = new Set<() => void>();
  private visibleOrderIds: Set<string> = new Set();
}

export const mapRenderer = new MapRenderer();
